import type { ComponentChildren } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

/**
 * A button's label that turns into its busy word without changing the button's size: both words sit
 * in one grid cell and the one not showing is hidden, so the button is as wide as the wider of the
 * two whether it is resting or working, and nothing beside it moves. The busy word blinks (the
 * skeletons' blink) from the frame the press sets `busy`. Hidden words are out of the accessibility
 * tree, so a screen reader hears the one showing. While busy, the button it sits in carries
 * aria-busy="true", which also covers a button whose aria-label names it in place of its words.
 */
export function BusyLabel({ busy, children, label = "Working…" }: { busy: boolean; children: ComponentChildren; label?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const host = ref.current?.closest("button, a, [role='button']");
    if (!host) return;
    if (busy) host.setAttribute("aria-busy", "true");
    else host.removeAttribute("aria-busy");
    return () => host.removeAttribute("aria-busy");
  }, [busy]);
  return (
    <span class="busy-label" data-busy={busy || undefined} ref={ref}>
      <span class="busy-rest">{children}</span>
      <span class="busy-work">{label}</span>
    </span>
  );
}
